import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { fetchAllProjectsAdminAction, deleteProjectAction, restoreProjectAction, permanentlyDeleteProjectAction } from '../../../utils/redux/project/projectActions';
import DataTable from '../../Reusables/DataTable/DataTable';
import { FiEdit2, FiTrash2, FiEye, FiRefreshCw, FiPlus } from 'react-icons/fi';
import { toast } from 'react-toastify';
import styles from './ProjectListing.module.css';

const ProjectListing = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { projects, isLoading, pagination } = useSelector(state => state.project);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [showDeleted, setShowDeleted] = useState(false);

    useEffect(() => {
        const filters = {};
        if (searchTerm) filters.search = searchTerm;
        if (statusFilter) filters.status = statusFilter;
        if (showDeleted) filters.showDeleted = true;
        dispatch(fetchAllProjectsAdminAction(pagination.page, pagination.limit, filters));
    }, [dispatch, pagination.page, pagination.limit, searchTerm, statusFilter, showDeleted]);

    const handleView = (id) => {
        navigate(`/projects/${id}`);
    };

    const handleEdit = (id) => {
        navigate(`/projects/${id}/edit`);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this project?')) {
            await dispatch(deleteProjectAction(id));
        }
    };

    const handleRestore = async (id) => {
        await dispatch(restoreProjectAction(id));
        dispatch(fetchAllProjectsAdminAction(pagination.page, pagination.limit, { showDeleted: true }));
    };

    const handlePermanentDelete = async (id) => {
        if (window.confirm('Are you sure you want to permanently delete this project? This action cannot be undone.')) {
            await dispatch(permanentlyDeleteProjectAction(id));
        }
    };

    const StatusBadge = ({ status }) => {
        const getStatusClass = () => {
            switch (status) {
                case 'In Progress': return styles.statusInProgress;
                case 'Approved': return styles.statusApproved;
                case 'Completed': return styles.statusCompleted;
                case 'Lost': return styles.statusLost;
                default: return '';
            }
        };

        return (
            <span className={`${styles.statusBadge} ${getStatusClass()}`}>
                {status}
            </span>
        );
    };

    const columns = [
        { Header: 'S.No.', accessor: 'index', width: 70 },
        { Header: 'Project Name', accessor: 'name', sortable: true },
        { Header: 'Project Code', accessor: 'project_code', sortable: true },
        {
            Header: 'Owner',
            accessor: (row) => row.owner?.first_name + ' ' + row.owner?.last_name || 'N/A',
        },
        {
            Header: 'Status',
            accessor: (row) => <StatusBadge status={row.status} />,
        },
        {
            Header: 'Boards',
            accessor: (row) => row.boards?.length || 0,
        },
        {
            Header: 'Created At',
            accessor: (row) => new Date(row.createdAt).toLocaleDateString(),
        },
        {
            Header: 'Actions',
            accessor: 'actions',
            width: 180,
            // The cell function now receives (value, row) where row is the full row object
            cell: (value, row) => (
                <div className={styles.actions}>
                    <button onClick={() => handleView(row._id)} className={`${styles.actionBtn} ${styles.viewBtn}`} title="View">
                        <FiEye size={16} />
                    </button>
                    {!row.isDeleted && (
                        <button onClick={() => handleEdit(row._id)} className={`${styles.actionBtn} ${styles.editBtn}`} title="Edit">
                            <FiEdit2 size={16} />
                        </button>
                    )}
                    {row.isDeleted ? (
                        <>
                            <button onClick={() => handleRestore(row._id)} className={`${styles.actionBtn} ${styles.restoreBtn}`} title="Restore">
                                <FiRefreshCw size={16} />
                            </button>
                            <button onClick={() => handlePermanentDelete(row._id)} className={`${styles.actionBtn} ${styles.permanentDeleteBtn}`} title="Permanently Delete">
                                <FiTrash2 size={16} />
                            </button>
                        </>
                    ) : (
                        <button onClick={() => handleDelete(row._id)} className={`${styles.actionBtn} ${styles.deleteBtn}`} title="Delete">
                            <FiTrash2 size={16} />
                        </button>
                    )}
                </div>
            ),
        },
    ];

    const tableData = projects.map((project, index) => ({
        ...project,
        index: ((pagination.page - 1) * pagination.limit) + index + 1,
    }));

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1 className={styles.title}>Projects</h1>
            </div>

            <div className={styles.filters}>
                <div className={styles.searchBox}>
                    <input
                        type="text"
                        placeholder="Search projects..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={styles.searchInput}
                    />
                </div>
                <div className={styles.filterGroup}>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className={styles.filterSelect}
                    >
                        <option value="">All Status</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Approved">Approved</option>
                        <option value="Completed">Completed</option>
                        <option value="Lost">Lost</option>
                    </select>
                </div>
                <div className={styles.filterGroup}>
                    <label className={styles.checkboxLabel}>
                        <input
                            type="checkbox"
                            checked={showDeleted}
                            onChange={(e) => setShowDeleted(e.target.checked)}
                        />
                        Show Deleted
                    </label>
                </div>
            </div>

            <DataTable
                columns={columns}
                data={tableData}
                loading={isLoading}
                pagination={pagination}
                onPageChange={(page) => dispatch(fetchAllProjectsAdminAction(page, pagination.limit, { search: searchTerm, status: statusFilter, showDeleted }))}
            />
        </div>
    );
};

export default ProjectListing;